// API =>
import { AxiosError } from 'axios';
import backEnd from "../../services/Api";

// Type =>
import {
    CreateAccountData, CreateAccountResponse,
    LoginData, LoginResponse,
} from "./CodeReceiveType";

// utils =>
import { LocalCSRFReceiver } from '../../utils/GetLocalCSRF';
import { FetchCSRF } from '../../utils/GetCSRF';

export const CreateAccount = async (
    data: CreateAccountData
): Promise<CreateAccountResponse> => {
    try {
        const csrf = await LocalCSRFReceiver();

        const response = await backEnd.post(
            "users/profile-create/",
            data,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrf,
                }
            }
        );

        return {
            success: true,
            data: response.data,
            message: "حساب کاربری با موفقیت ساخته شد",
        };

    } catch (error) {
        const axiosError = error as AxiosError<{ message?: string }>;

        if (axiosError.response?.data?.message) {
            return {
                success: false,
                message: axiosError.response.data.message,
            };
        }

        const status = axiosError.response?.status;

        const messages: Record<number, string> = {
            400: "اطلاعات وارد شده نامعتبر است",
            403: "دسترسی غیرمجاز. لطفاً صفحه را رفرش کنید",
            500: "خطای سرور. لطفاً دوباره تلاش کنید",
        };

        return {
            success: false,
            message: status ? messages[status] || "خطایی رخ داد" : "عدم ارتباط با سرور",
        };
    }
};

export const LoginAccount = async (
    data: LoginData
): Promise<LoginResponse> => {
    try {
        const csrf = await LocalCSRFReceiver();

        const response = await backEnd.post(
            "users/user-login/",
            {
                phone_number: data.phone_number,
                verification_token: data.verification_token
            },
            {
                headers: {
                    "X-CSRFToken": csrf,
                }
            },
        );

        return {
            success: true,
            message: "ورود با موفقیت انجام شد",
            data: response.data,
        };

    } catch (error) {
        const axiosError = error as AxiosError<{ message?: string }>;

        const status = axiosError.response?.status;

        const messages: Record<number, string> = {
            401: "اطلاعات وارد شده اشتباه است",
            404: "کاربری با این شماره پیدا نشد",
            403: "عدم مجوز. CSRF معتبر نیست",
            500: "خطای سرور. بعداً دوباره تلاش کنید",
        };

        return {
            success: false,
            message: status ? messages[status] || "خطا در ورود" : "عدم ارتباط با سرور",
        };
    }
};

export const LogoutAccount = async (
    data: LoginData
): Promise<LoginResponse> => {
    try {
        const csrf = await FetchCSRF();
        if (!csrf.success) return csrf;

        const response = await backEnd.post("users/user-logout/", {
            withCredentials: true
        });

        return {
            success: true,
            message: "خروج با موفقیت انجام شد",
            data: response.data,
        };

    } catch (error) {
        const axiosError = error as AxiosError;

        const status = axiosError.response?.status;

        const messages: Record<number, string> = {
            403: "CSRF معتبر نیست",
            404: "کاربر یافت نشد",
            500: "سرور در دسترس نیست",
        };

        return {
            success: false,
            message: status ? messages[status] || "خطا در خروج" : "عدم ارتباط با سرور",
        };
    }
};

export const SendOTP = async (phoneNumber: string) => {
    try {
        const response = await backEnd.post("users/send-otp/", {
            phone_number: phoneNumber,
        });

        alert(response.data.code);

        return {
            success: true,
            message: "کد با موفقیت ارسال شد",
        };

    } catch (error) {
        const axiosError = error as AxiosError<{ message?: string }>;

        const status = axiosError.response?.status;

        if (status === 429) {
            return {
                success: false,
                message: "درخواست‌های زیادی ارسال کردید. لطفاً چند دقیقه صبر کنید",
            };
        }

        if (axiosError.response?.data?.message) {
            return {
                success: false,
                message: axiosError.response.data.message,
            };
        }

        return {
            success: false,
            message: status ? "خطایی رخ داد" : "ارتباط با سرور برقرار نشد",
        };
    }
};

export const VerifyOTP = async (phoneNumber: string, code: string) => {
    try {
        const response = await backEnd.post("users/verify-otp/", {
            phone_number: phoneNumber,
            code: code
        });

        return {
            success: true,
            data: response.data,
            token: response.data.verification_token
        };

    } catch (error) {
        return {
            success: false,
            message: "کد وارد شده اشتباه است"
        };
    }
};

export const HandleOTPSubmit = async (
    phoneNumber: string,
    code: string,
    actionType: string,
    userData: any
): Promise<LoginResponse | CreateAccountResponse> => {

    const otpCheck = await VerifyOTP(phoneNumber, code);

    if (!otpCheck.success) {
        return {
            success: false,
            message: otpCheck.message ?? "خطا در تایید کد",
        };
    }

    const verificationToken = otpCheck.token;

    if (actionType === "sign-in") {
        return await CreateAccount({
            phone_number: phoneNumber,
            verification_token: verificationToken,
            ...userData,
        });
    }

    return await LoginAccount({
        phone_number: phoneNumber,
        verification_token: verificationToken 
    });
};
